/**
 * ACC-032 / PRI-020: download everything we hold, or ask for deletion within 30 days.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { DeletionStatus } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { Button } from "@lattice/ui/Button";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { downloadAccountData, loadDeletion, requestDeletion } from "../api.ts";
import { AccountShell } from "./Shell.tsx";
const DAY = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC"
});
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "ready";
  readonly deletion: DeletionStatus;
};
function day(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DAY.format(date);
}
export function AccountPrivacy() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [problem, setProblem] = useState<"download" | "delete" | null>(null);
  const load = () => {
    setScreen({
      kind: "loading"
    });
    void loadDeletion().then(loaded => {
      if (loaded === "signed_out") setScreen({
        kind: "signed_out"
      });else if (loaded === null) setScreen({
        kind: "failed"
      });else setScreen({
        kind: "ready",
        deletion: loaded
      });
    }, () => setScreen({
      kind: "failed"
    }));
  };
  useEffect(() => {
    document.title = "Your data";
    load();
  }, []);
  if (screen.kind === "loading") return <AccountShell title="Your data">
        <Pending message="Loading your data controls" bones={["title", "line", "control"]} />
      </AccountShell>;
  if (screen.kind === "signed_out") return <AccountShell title="Your data" signedOut>
        {null}
      </AccountShell>;
  if (screen.kind === "failed") return <AccountShell title="Your data">
        <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Try again in a minute. If it keeps happening, write to support.
        </p>
        <Button onClick={load}>Try again</Button>
      </AccountShell>;
  const download = async () => {
    setDownloading(true);
    setProblem(null);
    const ok = await downloadAccountData();
    setDownloading(false);
    if (!ok) setProblem("download");
  };
  const confirm = async () => {
    setRequesting(true);
    setProblem(null);
    const next = await requestDeletion();
    setRequesting(false);
    if (next === null) {
      setProblem("delete");
      return;
    }
    setConfirming(false);
    setScreen({
      kind: "ready",
      deletion: next
    });
  };
  const requested = screen.deletion.status === "requested";
  const due = screen.deletion.dueAt;
  return <AccountShell title="Your data">
      <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl" data-testid="account-privacy">
        <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Download a copy of everything we hold, or ask us to delete it.
        </p>
        {requested && due !== null && <Status state="ok" testId="deletion-requested">
            We'll delete your records by {day(due)}.
          </Status>}
        {screen.deletion.status === "completed" && <Status state="ok">Your records have been deleted.</Status>}
        {problem === "download" && <Status state="problem">We couldn't start the download. Try again.</Status>}
        {problem === "delete" && <Status state="problem">We couldn't send that request. Try again.</Status>}
        <Button testId="download-data" wide busy={downloading} onClick={() => void download()}>
          Download my data
        </Button>
        {requested || screen.deletion.status === "completed" ? null : confirming ? <>
            <p className="x1ghz6dp x1jty25b x1fzhlzt">
              We'll delete labs, answers and files within 30 days. Orders stay on file with your
              name removed.
            </p>
            <div className="x78zum5 x1a02dak x626il7">
              <Button variant="danger" testId="confirm-deletion" loading={requesting} onClick={() => void confirm()}>
                Delete my records
              </Button>
              <Button tone="quiet" onClick={() => setConfirming(false)}>
                Keep my records
              </Button>
            </div>
          </> : <Button tone="quiet" testId="request-deletion" onClick={() => setConfirming(true)}>
            Request deletion
          </Button>}
      </div>
    </AccountShell>;
}